import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Empty, Spin, Table, Typography,
} from 'antd';
import {
  ArrowRightOutlined,
  CalculatorOutlined,
  LineChartOutlined,
  RadarChartOutlined,
  LoadingOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import MuiTypography from '@mui/material/Typography';
import { motion } from 'framer-motion';

import apiClient from '../../../services/apiClient';
import PortfolioAnalyticsCharts from './PortfolioAnalyticsCharts';
import MyPortfolioHoldingsTable from './MyPortfolioHoldingsTable';
import { currency, fmt } from './portfolioHoldingPanels';
import { formatPortfolioType } from './portfolioAnalyticsUtils';
import { mpdCardSx } from './portfolioMuiTheme';

const { Text } = Typography;
const BASE_URL = process.env.REACT_APP_BASE_URL || '';

function StrategyBreakdownCard({ item, onNavigate }) {
  const strategy = item.strategy;
  const accent = strategy?.accentColor || '#9fe870';

  return (
    <Paper
      component={motion.article}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.35 }}
      elevation={0}
      sx={(theme) => ({
        ...mpdCardSx(theme),
        p: 2,
        height: '100%',
      })}
    >
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <MuiTypography component="span" sx={{ fontSize: 28, lineHeight: 1 }}>
          {strategy?.icon || '📊'}
        </MuiTypography>
        <Box>
          <MuiTypography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {strategy?.title || formatPortfolioType(item.type)}
          </MuiTypography>
          <MuiTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {strategy?.tagline || `${item.count} holding${item.count === 1 ? '' : 's'}`}
          </MuiTypography>
        </Box>
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <MuiTypography variant="caption" sx={{ color: 'text.secondary' }}>Portfolio weight</MuiTypography>
          <MuiTypography variant="caption" sx={{ fontWeight: 700 }}>{item.weightPct.toFixed(1)}%</MuiTypography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, item.weightPct)}
          sx={{
            height: 6,
            borderRadius: 99,
            bgcolor: `${accent}22`,
            '& .MuiLinearProgress-bar': { bgcolor: accent, borderRadius: 99 },
          }}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 1.5 }}>
        <Box>
          <MuiTypography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Market value
          </MuiTypography>
          <MuiTypography variant="body2" sx={{ fontWeight: 700 }}>{currency(item.marketValue)}</MuiTypography>
        </Box>
        <Box>
          <MuiTypography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Avg yield
          </MuiTypography>
          <MuiTypography variant="body2" sx={{ fontWeight: 700 }}>
            {item.avgYield != null ? `${Number(item.avgYield).toFixed(2)}%` : '—'}
          </MuiTypography>
        </Box>
      </Box>

      {strategy?.route && (
        <Button
          block
          type="default"
          onClick={() => onNavigate(strategy.route)}
          style={{ borderRadius: 8, fontWeight: 600 }}
        >
          Manage holdings →
        </Button>
      )}
    </Paper>
  );
}

function ToolCard({ icon, title, description, route, onNavigate }) {
  return (
    <button type="button" className="mpd-tool-card" onClick={() => onNavigate(route)}>
      <span className="mpd-tool-card-icon">{icon}</span>
      <span className="mpd-tool-card-title">{title}</span>
      <span className="mpd-tool-card-desc">{description}</span>
      <ArrowRightOutlined className="mpd-tool-card-arrow" />
    </button>
  );
}

const toNumberOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/[%,]/g, '').trim());
  return Number.isFinite(n) ? n : null;
};

const couponFrequencyLabel = (record) => {
  if (!record) return null;
  return record.couponPaymentFrequency
    || record.interestPaymentFrequency
    || record.compoundingFrequencyDetails
    || null;
};

async function fetchMasterIsinDetails(isin) {
  try {
    const res = await apiClient.get(
      `${BASE_URL}/master-isin/details/${encodeURIComponent(isin)}`,
      { validateStatus: () => true },
    );
    if (res.status === 200) return res.data?.data ?? null;
  } catch {
    // Fall back to holding-only inputs below.
  }
  return null;
}

async function fetchScanEvaluation(holding) {
  try {
    const details = await fetchMasterIsinDetails(holding.isin);

    const faceValue = toNumberOrNull(details?.faceValue ?? details?.facevalue) ?? 100;
    const couponRatePct = toNumberOrNull(holding.coupon) ?? toNumberOrNull(details?.couponRate);
    const maturityDate = holding.maturityDate
      || details?.maturityDate
      || details?.dateOfRedemption
      || undefined;

    const creditRatings = Array.isArray(details?.creditRatings)
      ? details.creditRatings
        .filter((r) => r?.currentRating)
        .map((r) => ({
          currentRating: r.currentRating,
          creditRatingAgencyName: r.creditRatingAgencyName,
        }))
      : undefined;

    const [priceRes, scanRes] = await Promise.all([
      apiClient.post(`${BASE_URL}/yield/market-price`, {
        isin: holding.isin,
        creditRatings: creditRatings?.length ? creditRatings : undefined,
        faceValue,
        couponRatePct: couponRatePct ?? undefined,
        maturityDate,
        couponPaymentFrequency: couponFrequencyLabel(details) || undefined,
      }),
      apiClient.get(`${BASE_URL}/scan/details/${encodeURIComponent(holding.isin)}`, {
        validateStatus: () => true,
      }),
    ]);

    const d = priceRes?.data?.data ?? priceRes?.data ?? {};
    const scanFromDetails = scanRes.status === 200
      ? toNumberOrNull(scanRes.data?.totalWeightedScore)
      : null;

    return {
      isin: holding.isin,
      issuer: holding.issuer,
      scanScore: toNumberOrNull(d.scanScore) ?? scanFromDetails,
      yieldByScan: toNumberOrNull(d.yieldByScan),
      marketPriceByScan: toNumberOrNull(d.marketPriceByScan),
      avgScan: toNumberOrNull(d.avgScan),
      ytmPercent: toNumberOrNull(d.ytmPercent) ?? toNumberOrNull(holding.yield),
      marketPrice: toNumberOrNull(d.marketPrice) ?? toNumberOrNull(holding.price),
    };
  } catch {
    return {
      isin: holding.isin,
      issuer: holding.issuer,
      scanScore: null,
      yieldByScan: null,
      marketPriceByScan: null,
      avgScan: null,
      ytmPercent: toNumberOrNull(holding.yield),
      marketPrice: toNumberOrNull(holding.price),
    };
  }
}

export default function PortfolioAnalyticsTab({
  analytics,
  holdings,
  loading,
  refreshing,
  onRequestReload,
  onRowsChanged,
  onNavigate,
}) {
  const navigate = useNavigate();
  const go = onNavigate || navigate;

  const [scanRows, setScanRows] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);

  const loadScanEvaluations = useCallback(async () => {
    if (!holdings?.length) {
      setScanRows([]);
      return;
    }
    setScanLoading(true);
    try {
      const results = await Promise.all(holdings.map(fetchScanEvaluation));
      setScanRows(results);
    } catch (e) {
      console.error('Failed to load SCAN evaluations', e);
      setScanRows([]);
    } finally {
      setScanLoading(false);
    }
  }, [holdings]);

  useEffect(() => {
    loadScanEvaluations();
  }, [loadScanEvaluations]);

  const scanColumns = useMemo(() => [
    { title: 'ISIN', dataIndex: 'isin', key: 'isin', width: 140, render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    { title: 'Issuer', dataIndex: 'issuer', key: 'issuer', ellipsis: true },
    {
      title: 'SCAN score',
      dataIndex: 'scanScore',
      key: 'scanScore',
      width: 100,
      align: 'right',
      render: (v) => (v != null ? Number(v).toFixed(2) : '—'),
    },
    {
      title: 'Yield by SCAN (%)',
      dataIndex: 'yieldByScan',
      key: 'yieldByScan',
      width: 130,
      align: 'right',
      render: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '—'),
    },
    {
      title: 'Price by SCAN (₹)',
      dataIndex: 'marketPriceByScan',
      key: 'marketPriceByScan',
      width: 130,
      align: 'right',
      render: (v) => fmt(v != null ? Number(v).toFixed(2) : null),
    },
    {
      title: 'Indicative YTM (%)',
      dataIndex: 'ytmPercent',
      key: 'ytmPercent',
      width: 120,
      align: 'right',
      render: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '—'),
    },
  ], []);

  if (!holdings?.length) {
    return <Empty description="Add bond holdings to view analytics" />;
  }

  return (
    <div className="mpd-tab-panel mpd-analytics-tab">
      <section className="mpd-analytics-section">
        <Typography.Title level={5} style={{ margin: '0 0 4px' }}>Portfolio charts</Typography.Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Allocation, maturity ladder, yield profile, and issuer concentration.
        </Text>
        <PortfolioAnalyticsCharts analytics={analytics} holdings={holdings} />
      </section>

      <section className="mpd-analytics-section">
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Box>
            <MuiTypography variant="h6" sx={{ fontWeight: 700 }}>By strategy</MuiTypography>
            <MuiTypography variant="body2" sx={{ color: 'text.secondary' }}>
              Each block reflects bonds saved under that construction strategy.
            </MuiTypography>
          </Box>
          <button
            type="button"
            className="mpd-secondary-btn"
            onClick={() => go('/fixed-income-portfolio')}
          >
            <ThunderboltOutlined /> AI portfolio builder
          </button>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {analytics.strategyBreakdown.map((item) => (
            <StrategyBreakdownCard
              key={item.type}
              item={item}
              onNavigate={go}
            />
          ))}
        </Box>
      </section>

      <section className="mpd-analytics-section">
        <Typography.Title level={5} style={{ margin: '0 0 4px' }}>
          <RadarChartOutlined style={{ marginRight: 8, color: '#6366f1' }} />
          SCAN evaluations
        </Typography.Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Per-ISIN risk-based pricing and SCAN score from market data.
        </Text>
        {scanLoading ? (
          <div className="mpd-tab-loading">
            <Spin indicator={<LoadingOutlined spin />} />
            <Text type="secondary">Loading SCAN evaluations…</Text>
          </div>
        ) : (
          <Table
            size="small"
            rowKey="isin"
            columns={scanColumns}
            dataSource={scanRows}
            pagination={scanRows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
            scroll={{ x: 720 }}
          />
        )}
      </section>

      <section className="mpd-analytics-section">
        <Typography.Title level={5} style={{ margin: '0 0 12px' }}>Tools</Typography.Title>
        <div className="mpd-tool-grid">
          <ToolCard
            icon={<LineChartOutlined />}
            title="Risk-based pricing"
            description="Benchmark curves, yield matrices, and SCAN-based fair value."
            route="/risk-based-pricing"
            onNavigate={go}
          />
          <ToolCard
            icon={<CalculatorOutlined />}
            title="Price–yield calculator"
            description="Compute YTM, current yield, and total return for any bond."
            route="/yield-calculator"
            onNavigate={go}
          />
        </div>
      </section>

      <section className="mpd-analytics-section mpd-holdings-inline">
        <Typography.Title level={5} style={{ margin: '0 0 4px' }}>All holdings</Typography.Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Complete list of ISINs — expand a row for position details and per-bond cash flows.
        </Text>
        <MyPortfolioHoldingsTable
          includeTypeColumn
          title="Portfolio holdings"
          emptyText="No holdings found."
          holdingsData={holdings}
          holdingsLoading={loading || refreshing}
          onRequestReload={onRequestReload}
          onRowsChanged={onRowsChanged}
        />
      </section>
    </div>
  );
}
